import { useCallback, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
// import { parseISO } from "date-fns";
import { useIsMutating } from "@tanstack/react-query";
import {
    FormCheckbox,
    RadioInput,
    ActionButton,
    Button,
    // DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    Input,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import {
    IEditEvaluationSubmissionNoteMedPayload,
    IGetClientNotes,
    ISessionNote,
    SessionLocations,
} from "../../../../modules/notes/types/notes.types";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import { EVALUATION_SUBMISSION_NOTE_MED_LABELS } from "../../../../utils/constants";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { IViewFile } from "../../../../utils/types";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import DeleteIcon from "../../../../icons/Delete";
import * as FilePicker from "../../../../shared-ui/FilePicker/FilePicker";
import * as Progress from "../../../../shared-ui/Progress/Progress";
import MedNoteEPrescribe from "../../../../modules/e-prescirbe/components/MedNoteEPrescribe/MedNoteEPrescribe";
import { useFetchUserDetails } from "../../../../hooks/queries/user";

interface EditEvaluationSubmissionNoteMedProps {
    noteDetails: ISessionNote;
}

export default function EditEvaluationSubmissionNoteMed({
    noteDetails,
}: EditEvaluationSubmissionNoteMedProps) {
    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedNote, setSelectedNote] = useState({} as IGetClientNotes);
    const [docInView, setDocInView] = useState<IViewFile | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
    /*
    const [localDate, setLocalDate] = useState<Date | null>(
        noteDetails.note_content.date
            ? parseISO(noteDetails.note_content.date)
            : null
    );
    */
    const isSigning = useRef(false);

    const { data: notes } = useFetchClientNotesList(
        noteDetails.client.client_id,
        {
            type: noteDetails.type,
            load_previous_notes: true,
            provider_id: noteDetails.provider.provider_id,
        }
    );
    const {
        handleAutoSaveNote,
        handleEditDraftNote,
        handleSignNote,
        handleAddNoteDocument,
        handleDeleteNoteContentDocument,
        uploadProgress,
    } = useEditNote();

    const isAddDocMutating = useIsMutating(["add-note-content-document"]);
    const isDeleteDocMutating = useIsMutating(["delete-note-content-document"]);

    const loggedInUser = useFetchUserDetails();

    const loggedInUserName = loggedInUser?.isSuccess
        ? `${loggedInUser?.data?.first_name} ${loggedInUser?.data?.last_name}`
        : "";

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        watch,
        control,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditEvaluationSubmissionNoteMedPayload>({
        resolver: yupResolver(
            yup.object({
                location_of_the_client: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Client location is required")
                            : field
                    ),
                other_location_of_the_client: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_client"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other client location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Provider location is required")
                            : field
                    ),
                other_location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_provider"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other provider location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                consent_was_received_for_the_teletherapy_session: yup.bool(),
                the_session_was_conducted_via_video: yup.bool(),
                session_location: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("Session location is required"),
                    otherwise: yup.string(),
                }),
                /*
                date: yup
                    .string()
                    .nullable()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .required("Date is required"),
                        otherwise: yup.string().nullable(),
                    }),
                start_time: yup.string().nullable(),
                end_time: yup
                    .string()
                    .nullable()
                    .test(
                        "end-time-after-start-time",
                        "End time cannot be earlier than start time",
                        (endTime, context) => {
                            const { start_time: startTime } = context.parent;

                            return (
                                !startTime || !endTime || endTime >= startTime
                            );
                        }
                    ),
                */
                diagnosis_codes: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .array()
                            .min(1, "Primary diagnosis is required"),
                        otherwise: yup.array(),
                    }),
                secondary_diagnosis: yup.array().max(1).nullable(),
                tertiary_diagnosis: yup.array().max(1).nullable(),
                additional_diagnosis_one: yup.array().max(1).nullable(),
                additional_diagnosis_two: yup.array().max(1).nullable(),
                summary: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                name_of_provider_therapist: yup.string(),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: {
            location_of_the_client:
                noteDetails.note_content.location_of_the_client,
            other_location_of_the_client:
                noteDetails.note_content.other_location_of_the_client,
            location_of_the_provider:
                noteDetails.note_content.location_of_the_provider,
            other_location_of_the_provider:
                noteDetails.note_content.other_location_of_the_provider,
            consent_was_received_for_the_teletherapy_session:
                noteDetails.note_content
                    .consent_was_received_for_the_teletherapy_session,
            the_session_was_conducted_via_video:
                noteDetails.note_content.the_session_was_conducted_via_video,
            session_location: noteDetails.note_content.session_location ?? "",
            date: noteDetails.note_content.date ?? null,
            start_time: noteDetails.note_content.start_time ?? null,
            end_time: noteDetails.note_content.end_time ?? null,
            diagnosis_codes: noteDetails.note_content.diagnosis_codes ?? [],
            secondary_diagnosis: noteDetails.note_content.secondary_diagnosis
                ? [noteDetails.note_content.secondary_diagnosis]
                : undefined,
            tertiary_diagnosis: noteDetails.note_content.tertiary_diagnosis
                ? [noteDetails.note_content.tertiary_diagnosis]
                : undefined,
            additional_diagnosis_one: noteDetails.note_content
                .additional_diagnosis_one
                ? [noteDetails.note_content.additional_diagnosis_one]
                : undefined,
            additional_diagnosis_two: noteDetails.note_content
                .additional_diagnosis_two
                ? [noteDetails.note_content.additional_diagnosis_two]
                : undefined,
            summary: noteDetails.note_content.summary ?? "",
            name_of_provider_therapist:
                noteDetails.note_content.name_of_provider_therapist ??
                loggedInUserName ??
                "",
            signature: noteDetails.note_content.signature ?? "",
        },
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue(
            "location_of_the_client",
            note.note_content.location_of_the_client
        );
        setValue(
            "other_location_of_the_client",
            note.note_content.other_location_of_the_client
        );
        setValue(
            "location_of_the_provider",
            note.note_content.location_of_the_provider
        );
        setValue(
            "other_location_of_the_provider",
            note.note_content.other_location_of_the_provider
        );
        setValue(
            "consent_was_received_for_the_teletherapy_session",
            note.note_content.consent_was_received_for_the_teletherapy_session
        );
        setValue(
            "the_session_was_conducted_via_video",
            note.note_content.the_session_was_conducted_via_video
        );

        setValue("session_location", note.note_content.session_location ?? "");
        const date = note.note_content.date ?? null;

        setValue("date", date);
        // setLocalDate(date ? parseISO(date) : null);
        setValue("start_time", note.note_content.start_time ?? null);
        setValue("end_time", note.note_content.end_time ?? null);
        setValue("diagnosis_codes", note.note_content.diagnosis_codes ?? []);
        setValue(
            "secondary_diagnosis",
            note.note_content.secondary_diagnosis
                ? [note.note_content.secondary_diagnosis]
                : undefined
        );
        setValue(
            "tertiary_diagnosis",
            note.note_content.tertiary_diagnosis
                ? [note.note_content.tertiary_diagnosis]
                : undefined
        );
        setValue(
            "additional_diagnosis_one",
            note.note_content.additional_diagnosis_one
                ? [note.note_content.additional_diagnosis_one]
                : undefined
        );
        setValue(
            "additional_diagnosis_two",
            note.note_content.additional_diagnosis_two
                ? [note.note_content.additional_diagnosis_two]
                : undefined
        );
        setValue("summary", note.note_content.summary ?? "");
        setValue(
            "name_of_provider_therapist",
            note.note_content.name_of_provider_therapist ?? ""
        );
        setShowPrompt(false);
    };

    const onDrop = useCallback((acceptedFiles: File[]) => {
        setSelectedFiles((files) => [...acceptedFiles, ...(files ?? [])]);
    }, []);

    const sessionLocationOptionsForSelect = Object.keys(SessionLocations).map(
        (location) => ({
            label: removeEnumUnderscore(location),
            value: SessionLocations[location as keyof typeof SessionLocations],
        })
    );

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            {docInView ? (
                <Dialog open>
                    <DialogContent
                        title={docInView.document_name ?? "Note attachment"}
                        handleCloseDialog={() => setDocInView(null)}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={docInView.metadata?.content_type ?? ""}
                            title={docInView.document_name ?? "Note attachment"}
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />
            {notes?.data.length ? (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes.data.map((note) => (
                                <DropdownItem
                                    key={note.note_id}
                                    onClick={() => {
                                        setSelectedNote(note);
                                        setShowPrompt(true);
                                    }}
                                >
                                    {makeStringFirstLetterCapital(
                                        removeEnumUnderscore(note.note_type)
                                    )}{" "}
                                    - Note content Details -{" "}
                                    {formatDate(note.date_of_service)}
                                </DropdownItem>
                            ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            ) : null}
            <form
                id="edit-note-form"
                aria-label="evaluation submission note form"
                onSubmit={handleSubmit(handleSignNote)}
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <Controller
                        name="session_location"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    EVALUATION_SUBMISSION_NOTE_MED_LABELS.session_location
                                }
                                placeholder=""
                                options={sessionLocationOptionsForSelect}
                                onChange={(val) =>
                                    field.onChange(
                                        (val as { value: string }).value
                                    )
                                }
                                value={
                                    sessionLocationOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                hasError={!!errors.session_location}
                                errorText={errors.session_location?.message}
                            />
                        )}
                    />
                </div>
                {watch("session_location") === SessionLocations.Telehealth && (
                    <>
                        <h3 className="note-label-req">
                            {
                                EVALUATION_SUBMISSION_NOTE_MED_LABELS.location_of_the_client
                            }
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_client")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_client === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_client ? (
                            <div className="note-error">
                                <p>{errors.location_of_the_client?.message}</p>
                            </div>
                        ) : null}
                        {watch("location_of_the_client") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_client"
                                    )}
                                    label={
                                        EVALUATION_SUBMISSION_NOTE_MED_LABELS.other_location_of_the_client
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_client
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_client
                                    }
                                    errorText={
                                        errors.other_location_of_the_client
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <h3 className="note-label-req">
                            {
                                EVALUATION_SUBMISSION_NOTE_MED_LABELS.location_of_the_provider
                            }
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_provider")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_provider === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_provider ? (
                            <div className="note-error">
                                <p>
                                    {errors.location_of_the_provider?.message}
                                </p>
                            </div>
                        ) : null}
                        {watch("location_of_the_provider") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_provider"
                                    )}
                                    label={
                                        EVALUATION_SUBMISSION_NOTE_MED_LABELS.other_location_of_the_provider
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_provider
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_provider
                                    }
                                    errorText={
                                        errors.other_location_of_the_provider
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <div className="note-checkboxes">
                            <FormCheckbox
                                {...register(
                                    "consent_was_received_for_the_teletherapy_session"
                                )}
                                label={
                                    EVALUATION_SUBMISSION_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.consent_was_received_for_the_teletherapy_session
                                }
                            />
                            <FormCheckbox
                                {...register(
                                    "the_session_was_conducted_via_video"
                                )}
                                label={
                                    EVALUATION_SUBMISSION_NOTE_MED_LABELS.the_session_was_conducted_via_video
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.the_session_was_conducted_via_video
                                }
                            />
                        </div>
                    </>
                )}

                <div className="fg">
                    <Controller
                        name="diagnosis_codes"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    EVALUATION_SUBMISSION_NOTE_MED_LABELS.diagnosis_codes
                                }
                                value={field.value}
                                onChange={field.onChange}
                                isRequired
                                errorText={
                                    errors.diagnosis_codes?.type === "typeError"
                                        ? "Diagnosis codes are required"
                                        : errors.diagnosis_codes?.message
                                }
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="secondary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    EVALUATION_SUBMISSION_NOTE_MED_LABELS.secondary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.secondary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    EVALUATION_SUBMISSION_NOTE_MED_LABELS.tertiary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.tertiary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_one"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    EVALUATION_SUBMISSION_NOTE_MED_LABELS.additional_diagnosis_one
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_one?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_two"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    EVALUATION_SUBMISSION_NOTE_MED_LABELS.additional_diagnosis_two
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_two?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>

                <MedNoteEPrescribe
                    noteDetails={noteDetails}
                    showDiagnosisSync
                />
                {/*
                <div className="fg">
                    <Controller
                        name="date"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                label={
                                    EVALUATION_SUBMISSION_NOTE_MED_LABELS.date
                                }
                                isRequired
                                selected={localDate}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                onChange={(date) => {
                                    field.onChange(date ?? null);
                                    setLocalDate(date);
                                }}
                                hasError={Boolean(errors.date)}
                                errorText={errors.date?.message}
                            />
                        )}
                    />
                </div>
                <div className="fg fg-space-between two flex">
                    <Input
                        {...register("start_time", {
                            onChange: async () => {
                                await trigger("end_time");
                            },
                        })}
                        type="time"
                        label={EVALUATION_SUBMISSION_NOTE_MED_LABELS.start_time}
                        isOptional
                    />
                    <Input
                        {...register("end_time")}
                        type="time"
                        label={EVALUATION_SUBMISSION_NOTE_MED_LABELS.end_time}
                        hasError={Boolean(errors.end_time)}
                        errorText={errors.end_time?.message}
                        isOptional
                    />
                </div>
                */}
                <div className="fg">
                    <TextArea
                        {...register("summary")}
                        label={EVALUATION_SUBMISSION_NOTE_MED_LABELS.summary}
                        isRequired
                        hasError={!!errors.summary}
                        errorText={errors.summary?.message}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("name_of_provider_therapist")}
                        label={
                            EVALUATION_SUBMISSION_NOTE_MED_LABELS.name_of_provider_therapist
                        }
                        isOptional
                    />
                </div>
                <h3 className="note-label-opt">
                    {EVALUATION_SUBMISSION_NOTE_MED_LABELS.files}
                </h3>
                <div className="fg flex flex-col gap-y-[--spacing-form-items]">
                    <FilePicker.FilePicker
                        onDrop={onDrop}
                        disabled={isAddDocMutating > 0}
                    >
                        <FilePicker.FilePickerIcon />
                        <FilePicker.FilePickerMeta description="Click to upload note files" />
                    </FilePicker.FilePicker>
                    {selectedFiles?.length ? (
                        <div className="flex flex-col gap-y-[--spacing-items]">
                            <FilePicker.FilePickerSelectedFiles
                                selectedFiles={selectedFiles}
                                handleRemoveSelectedFile={(filename) =>
                                    setSelectedFiles((files) => {
                                        const remaining =
                                            files?.filter(
                                                (file) => file.name !== filename
                                            ) ?? [];
                                        return remaining.length
                                            ? remaining
                                            : null;
                                    })
                                }
                                wrapperClassName="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] grid-flow-row flex-row gap-[--spacing-items]"
                            />
                            <Button
                                className="self-start"
                                onClick={() =>
                                    handleAddNoteDocument(selectedFiles, () =>
                                        setSelectedFiles(null)
                                    )
                                }
                                disabled={isAddDocMutating > 0}
                            >
                                Save files
                            </Button>
                            {uploadProgress > 0 && uploadProgress < 100 ? (
                                <Progress.Root value={uploadProgress}>
                                    <Progress.Label>
                                        Uploading {selectedFiles.length}{" "}
                                        {selectedFiles.length === 1
                                            ? "file"
                                            : "files"}
                                        ...
                                    </Progress.Label>
                                    <Progress.Value />
                                    <Progress.Track>
                                        <Progress.Indicator />
                                    </Progress.Track>
                                </Progress.Root>
                            ) : null}
                        </div>
                    ) : null}
                    {noteDetails.note_content_documents?.map((doc) => (
                        <div
                            key={doc.note_content_document_id}
                            className="flex items-center gap-x-3"
                        >
                            <ActionButton
                                variant="can-hover"
                                size="auto"
                                className="h-7 !text-text text-caption underline"
                                actionTitle="View file"
                                onClick={() => setDocInView(doc)}
                            >
                                {`${displayNameInRightFormat(
                                    {
                                        firstName: doc.uploaded_by.first_name,
                                        lastName: doc.uploaded_by.last_name,
                                    },
                                    false
                                )} - ${doc.document_name}`}
                            </ActionButton>
                            <ActionButton
                                variant="can-hover"
                                aria-label="delete icon"
                                size="auto"
                                className="h-7"
                                actionTitle="Delete file from note content"
                                disabled={isDeleteDocMutating > 0}
                                onClick={() =>
                                    handleDeleteNoteContentDocument(
                                        doc.note_content_document_id
                                    )
                                }
                            >
                                <DeleteIcon />
                            </ActionButton>
                        </div>
                    ))}
                </div>
                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            label="Provider's Initials"
                            autoComplete="off"
                            hasError={Boolean(errors.signature)}
                            errorText={errors.signature?.message}
                            isRequired
                        />
                    </div>
                </div>
                <EditNoteFooter
                    onSaveButtonClick={() => {
                        isSigning.current = false;
                        handleSubmit(handleEditDraftNote)();
                    }}
                    noteDOS={noteDetails.date_of_service}
                    onSubmitButtonClick={() => {
                        isSigning.current = true;
                    }}
                />
            </form>
        </>
    );
}
